import { MouseEvent } from 'react';
import useAssetLibrary from '~/common/useAssetLibrary';
import { IImage } from '~/types/model.types';
import { THUMBNAIL_CROP_RATIO } from '~/components/Builder/Configure/SetThumbnail/SetThumbnailPreview';
import { AcceptedFileFormats } from '~/common/consts';
import { useModal } from '~/providers/modal.provider';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { isReactFirst } from '~/utils/environment.utils';
import { TServerImageCrop } from '~/types/server.types';
import { cloneDeep } from 'lodash';

interface Props {
    customModalCssClass?: string;
    modalTitle?: string;
    onClickEditThumbnail?: (event: MouseEvent) => void;
    onSet: (image: IImage) => void;
    thumbnailImage: IImage | null;
    aspectRatio?: number;
}

export const useFlowThumbnail = ({
    customModalCssClass,
    modalTitle,
    onClickEditThumbnail,
    onSet,
    thumbnailImage,
    aspectRatio = THUMBNAIL_CROP_RATIO
}: Props) => {
    const { openAttachAssetModal } = useAssetLibrary();
    const { openGlobalModal, closeGlobalModal } = useModal();
    const { t: tGlobal } = useTranslation(NAMESPACES.GLOBALS);

    const openThumbnailModalReact = (event?: MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        onClickEditThumbnail?.(event);

        openGlobalModal('AttachAssetModalWrapper', {
            config: {
                title: modalTitle,
                defaultTab: thumbnailImage ? 'crop' : 'upload',
                cropTab: {
                    enabled: true,
                    selectedImage: thumbnailImage,
                    aspectRatio,
                    onSubmitCrop: (
                        image: IImage,
                        reactCropPropsTransform?: TServerImageCrop
                    ) => {
                        onSet({
                            ...cloneDeep(image),
                            ...(reactCropPropsTransform || {})
                        });
                    },
                    onCancelCrop: () =>
                        closeGlobalModal('AttachAssetModalWrapper')
                },
                uploadTab: {
                    enabled: true,
                    allowMultiple: false,
                    uploadType: 'cover',
                    acceptFilesFilter: AcceptedFileFormats.CloudinarySupported,
                    onFileUpload: (image: IImage) => {
                        onSet({ ...image, crop_ratio: `${aspectRatio}` });
                    },
                    uploadTabText: {
                        dropFileText: tGlobal(
                            'upload_pane.image.drop_image_here'
                        ),
                        chooseFileText: tGlobal(
                            'upload_pane.image.choose_image'
                        )
                    }
                },
                galleryTab: {
                    enabled: true,
                    allowMultiple: false
                }
            }
        });
    };

    const openThumbnailModalNg = async (event?: MouseEvent<HTMLElement>) => {
        event?.stopPropagation();

        onClickEditThumbnail?.(event);

        const modalOptions = [
            true,
            true,
            true,
            thumbnailImage ? 'Crop' : 'Upload',
            false,
            false,
            thumbnailImage || null,
            aspectRatio,
            true,
            false,
            'image/*',
            '',
            customModalCssClass,
            !!modalTitle, // showCropTitle
            '', // footNoteText
            false, // branded
            '', // footNoteTextUploadTabDesktop
            '' // footNoteTextUploadTabMobile
        ];

        try {
            const selectedImage = (await openAttachAssetModal(
                modalOptions
            )) as IImage;

            const updatedImage = {
                ...selectedImage,
                crop_ratio: `${aspectRatio}`
            };

            onSet(updatedImage);
        } catch {}
    };

    const openThumbnailModal = (event?: MouseEvent<HTMLElement>) => {
        return isReactFirst()
            ? openThumbnailModalReact(event)
            : openThumbnailModalNg(event);
    };

    return { openThumbnailModal };
};
